import { useState } from "react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Product's live modifier groups: prices, owner defaults, sold-out options disabled, min/max respected.
export function OptionPicker({ item, onAdd, onCancel, initial = null, actionLabel = "Add", qty = 1 }) {
  const [one, setOne] = useState(false);
  const [sel, setSel] = useState(() => (initial ? Object.fromEntries(item.groups.map((g) => [g.id, g.options.filter((o) => initial.includes(o.id)).map((o) => o.id)]))
    : Object.fromEntries(item.groups.filter((g) => g.required)
      .map((g) => [g.id, (g.options || []).filter((o) => o.is_default && o.available !== false).slice(0, 1).map((o) => o.id)]).filter(([, v]) => v.length))));
  const picked = Object.values(sel).flat();
  const shown = item.groups.filter((g) => !g.combo_gate || g.combo_gate.some((id) => picked.includes(id))); // combo choices only with the combo
  const live = shown.map((g) => g.id);
  const total = item.price + shown.reduce((t, g) => t + g.options.filter((o) => (sel[g.id] || []).includes(o.id)).reduce((a, o) => a + o.price, 0), 0);
  const toggle = (g, o) => setSel((s) => {
    const cur = s[g.id] || [];
    if (cur.includes(o.id)) return { ...s, [g.id]: cur.filter((x) => x !== o.id) };
    return { ...s, [g.id]: g.max === 1 ? [o.id] : [...cur, o.id].slice(0, g.max || 9) };
  });
  const ok = shown.every((g) => !g.required || (sel[g.id] || []).length >= (g.min || 1));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="kiosk-option-picker">
      <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[86vh] overflow-y-auto p-6 kiosk-rise">
        <div className="flex justify-between items-baseline mb-4"><div className="text-2xl font-bold font-heading text-[#0f2a3d]">{item.name}</div><div className="text-lg font-semibold tabular-nums text-[#0f2a3d]" data-testid="kiosk-option-total">{fmt(total)}</div></div>
        {shown.map((g) => (
          <div key={g.id} className="mb-4">
            <div className="text-sm font-semibold text-[#5c7385] mb-2">{g.name}{g.required ? " · required" : g.combo_gate ? " · comes with your combo" : " · optional"}{g.max > 1 ? ` · up to ${g.max}` : ""}</div>
            <div className="flex flex-wrap gap-2">
              {g.options.map((o) => {
                const on = (sel[g.id] || []).includes(o.id);
                return (
                  <button key={o.id} data-testid={`kiosk-option-${o.id}`} onClick={() => toggle(g, o)} disabled={o.available === false && !on}
                    className={`px-4 h-12 rounded-full text-base border transition-colors disabled:opacity-40 disabled:line-through ${on ? "bg-[#0f2a3d] text-white border-transparent" : "bg-white border-[rgba(15,42,61,0.15)] text-[#0f2a3d]"}`}>
                    {o.name}{o.price ? ` +${fmt(o.price)}` : ""}{o.is_default ? " · default" : ""}{o.available === false ? " · sold out" : ""}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {qty > 1 && (
          <div className="flex gap-2 mb-2" data-testid="kiosk-option-scope">
            {[[false, `Change all ${qty}`], [true, "Change just 1"]].map(([v, l]) => (
              <button key={l} data-testid={`kiosk-option-scope-${v ? "one" : "all"}`} onClick={() => setOne(v)}
                className={`flex-1 h-11 rounded-full text-sm font-semibold border ${one === v ? "bg-[#0f2a3d] text-white border-transparent" : "bg-white text-[#0f2a3d] border-[rgba(15,42,61,0.15)]"}`}>{l}</button>
            ))}
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <button className="flex-1 h-14 rounded-full bg-[#eef2f5] text-[#0f2a3d] text-lg" data-testid="kiosk-option-cancel" onClick={onCancel}>Cancel</button>
          <button className="flex-1 h-14 rounded-full bg-[#e2572b] text-white text-lg font-semibold disabled:opacity-40" disabled={!ok}
            data-testid="kiosk-option-add" onClick={() => onAdd(shown.flatMap((g) => sel[g.id] || []), one)}>{actionLabel}</button>
        </div>
      </div>
    </div>
  );
}
